/**
 * Integrytas Group - Base App (MOBILE)
 * Page: DOC.GENERATED — mobile-native.
 *
 * Touch-first counterpart of src/pages/doc/DocGeneratedPage.tsx. A DISTINCT
 * presentation (dense card list + bottom-sheet detail) over the SAME read layer:
 * it reads the generated-document queue via apiDb (doc_generated), resolving the
 * template FK to its name at fetch time (§17.B4 — never the raw doc_template_id).
 * The screen is READ-ONLY — status/format are CHECK-enum codes rendered through
 * localized labelled constants (§17.B11), dates through the locale formatter. The
 * desktop "Download" action depends on a signed-URL/window flow with no phone
 * equivalent and is NOT a Common-Routine write, so it stays desktop-only; there
 * is therefore no routed_writes import here. §16: never the desktop grid re-hosted.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Inbox, X, FileText } from 'lucide-react';
import { apiDb } from '../../lib/api_client';
import { useActiveLocale } from '../../lib/use_active_locale';
import { useFormatDateTime } from '../../lib/format_date';
import {
  MobilePageHeader, MobileContentCard, MobileCardRow, MobileDetailRow, MobileFilterChips,
  MobileStatusPill, type MobilePillTone, type MobileLeadTone,
} from '../components';
import { MobileRelatedChildren } from '../components/MobileRelatedChildren';
import type { MobilePageComponentProps } from '../mobile_page_components';

// ── CHECK-enum labelled constants (§17.B11) — the stored value is the code; the
// label is localized and is the only thing rendered in cells/chips.
interface EnumOpt { value: string; labelKey: string; fallback: string }

const STATUSES: EnumOpt[] = [
  { value: 'REQUESTED', labelKey: 'doc.gen_status.REQUESTED', fallback: 'Requested' },
  { value: 'RENDERED', labelKey: 'doc.gen_status.RENDERED', fallback: 'Rendered' },
  { value: 'FAILED', labelKey: 'doc.gen_status.FAILED', fallback: 'Failed' },
];

const OUTPUT_FORMATS: EnumOpt[] = [
  { value: 'PDF', labelKey: 'doc.output_format.PDF', fallback: 'PDF' },
  { value: 'HTML', labelKey: 'doc.output_format.HTML', fallback: 'HTML' },
  { value: 'XLSX', labelKey: 'doc.output_format.XLSX', fallback: 'Excel (XLSX)' },
  { value: 'CSV', labelKey: 'doc.output_format.CSV', fallback: 'CSV' },
];

interface MobileGeneratedRow {
  doc_generated_id: number;
  title: string | null;
  template_name: string; // resolved from doc_template.name / internal_code
  status: string;
  output_format: string | null;
  page_count: number | null;
  record_id: string | null;
  created_at: string;
}

type StatusFacet = 'all' | 'REQUESTED' | 'RENDERED' | 'FAILED';

// Status → mobile pill / lead tone. RENDERED reads success, FAILED danger,
// REQUESTED is an in-progress "waiting" state.
function statusTone(status: string): MobilePillTone {
  if (status === 'RENDERED') return 'ok';
  if (status === 'FAILED') return 'no';
  return 'wait';
}
function statusLeadTone(status: string): MobileLeadTone {
  if (status === 'RENDERED') return 'green';
  if (status === 'FAILED') return 'red';
  return 'amber';
}

export function MobileDocGeneratedPage({ session: _session }: MobilePageComponentProps) {
  const { t } = useTranslation();
  const activeLocale = useActiveLocale();
  const formatDateTime = useFormatDateTime();
  const [rows, setRows] = useState<MobileGeneratedRow[]>([]);
  const [filter, setFilter] = useState('');
  const [statusFacet, setStatusFacet] = useState<StatusFacet>('all');
  const [selected, setSelected] = useState<MobileGeneratedRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const statusLabel = (v: string) => {
    const o = STATUSES.find((s) => s.value === v);
    return o ? t(o.labelKey, o.fallback) : v;
  };
  const formatLabel = (v: string | null) => {
    if (!v) return t('common.none', 'None');
    const o = OUTPUT_FORMATS.find((s) => s.value === v);
    return o ? t(o.labelKey, o.fallback) : v;
  };

  const reload = async () => {
    setBusy(true);
    setError(null);
    try {
      // Resolve the template FK to its name at fetch time (§17.B4) — never show
      // the raw doc_template_id. doc_template carries `internal_code` + `name`.
      const { data, error: e } = await apiDb.from('doc_generated').select(`
        doc_generated_id, title, status, output_format, page_count, record_id, created_at,
        doc_template ( internal_code, name )
      `).order('created_at', { ascending: false }).limit(500);
      if (e) throw e;
      type Raw = Omit<MobileGeneratedRow, 'template_name'> & {
        doc_template?: { internal_code: string; name: string | null } | null;
      };
      const mapped: MobileGeneratedRow[] = ((data ?? []) as unknown as Raw[]).map((r) => ({
        doc_generated_id: r.doc_generated_id,
        title: r.title,
        template_name: r.doc_template?.name ?? r.doc_template?.internal_code ?? '',
        status: r.status,
        output_format: r.output_format,
        page_count: r.page_count,
        record_id: r.record_id,
        created_at: r.created_at,
      }));
      setRows(mapped);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // Re-run on mount and whenever the active locale changes (enum labels + dates
  // follow it). `t`/formatDateTime are intentionally NOT deps — see hooks rule.
  useEffect(() => { void reload(); }, [activeLocale]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFacet !== 'all' && r.status !== statusFacet) return false;
      if (!q) return true;
      return (
        (r.title ?? '').toLowerCase().includes(q) ||
        r.template_name.toLowerCase().includes(q) ||
        (r.record_id ?? '').toLowerCase().includes(q)
      );
    });
  }, [rows, filter, statusFacet]);

  const facets: { key: StatusFacet; label: string }[] = [
    { key: 'all', label: t('mobile.doc_generated.all', 'All') },
    { key: 'REQUESTED', label: statusLabel('REQUESTED') },
    { key: 'RENDERED', label: statusLabel('RENDERED') },
    { key: 'FAILED', label: statusLabel('FAILED') },
  ];

  const titleOf = (r: MobileGeneratedRow) => r.title || t('common.none', 'None');
  const pagesLabel = (n: number | null) =>
    n == null ? t('common.none', 'None') : t('mobile.doc_generated.pages_count', { count: n, defaultValue: `${n} pages` });

  return (
    <section className="m-screen">
      <MobilePageHeader
        title={t('mobile.doc_generated.title', 'Generated Documents')}
        description={t('mobile.doc_generated.description', 'Documents rendered from templates, their format and status.')}
      />

      <div className="m-searchbox">
        <Search />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('mobile.doc_generated.search', 'Search documents')}
          aria-label={t('mobile.doc_generated.search', 'Search documents')}
        />
      </div>

      <MobileFilterChips
        chips={facets.map((f) => ({ value: f.key, label: f.label }))}
        isActive={(v) => statusFacet === v}
        onSelect={(v) => setStatusFacet(v as StatusFacet)}
      />

      {error && (
        <MobileContentCard>
          <div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div>
        </MobileContentCard>
      )}

      {filtered.length === 0 && !busy ? (
        <MobileContentCard><div className="m-empty"><Inbox /> {t('mobile.doc_generated.none', 'No generated documents')}</div></MobileContentCard>
      ) : (
        <MobileContentCard list>
          {filtered.map((r) => (
            <MobileCardRow
              key={r.doc_generated_id}
              lead={<FileText />}
              leadTone={statusLeadTone(r.status)}
              title={titleOf(r)}
              subtitle={`${r.template_name || t('common.none', 'None')} · ${formatLabel(r.output_format)}`}
              trailing={<MobileStatusPill tone={statusTone(r.status)}>{statusLabel(r.status)}</MobileStatusPill>}
              chevron
              onClick={() => setSelected(r)}
            />
          ))}
        </MobileContentCard>
      )}

      {/* Detail bottom-sheet */}
      <div className={'m-scrim' + (selected ? ' m-open' : '')} onClick={() => setSelected(null)} />
      <div className={'m-sheet' + (selected ? ' m-open' : '')} aria-label={t('mobile.doc_generated.detail', 'Generated document detail')}>
        <div className="m-grabber" />
        {selected && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><FileText size={20} /></span>
              <span>
                <div className="m-nm">{titleOf(selected)}</div>
                <div className="m-em">{selected.template_name || t('common.none', 'None')}</div>
              </span>
            </div>
            <MobileContentCard>
              <MobileDetailRow
                label={t('mobile.doc_generated.status', 'Status')}
                value={<MobileStatusPill tone={statusTone(selected.status)}>{statusLabel(selected.status)}</MobileStatusPill>}
              />
              <MobileDetailRow label={t('mobile.doc_generated.template', 'Template')} value={selected.template_name || t('common.none', 'None')} />
              <MobileDetailRow label={t('mobile.doc_generated.format', 'Format')} value={formatLabel(selected.output_format)} />
              <MobileDetailRow label={t('mobile.doc_generated.pages', 'Pages')} value={pagesLabel(selected.page_count)} />
              <MobileDetailRow
                label={t('mobile.doc_generated.record', 'Record')}
                /* fk-display-ok: record_id is the natural id of the audited source row */
                value={selected.record_id ?? t('common.none', 'None')}
              />
              <MobileDetailRow label={t('mobile.doc_generated.requested', 'Requested')} value={formatDateTime(selected.created_at)} />
            </MobileContentCard>
            {/* §17.B13 — the row's related records, stacked one group per block. */}
            <MobileContentCard>
              <MobileRelatedChildren groups={[
                  { key: 'template', title: t('mobile.doc_generated.related.template', 'Template'),
                  items: selected.template_name ? [{ id: 'template', label: selected.template_name }] : [] },
                ]} />
            </MobileContentCard>
            <div className="m-actionbar">
              <button type="button" className="m-btn m-btn-ghost" onClick={() => setSelected(null)} aria-label={t('mobile.close', 'Close')}>
                <X size={16} />
                {t('mobile.close', 'Close')}
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
